import React, { useState, useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getUserIdByUsername, trimUsername } from '../lib/usernames';
import { getOtherProfile } from '../lib/avatars';
import P2PChat from './P2PChat';
import RoomChat from './RoomChat';
import DMChat from './DMChat';
import FriendsPanel from './FriendsPanel';
import InvitesPanel from './InvitesPanel';
import ContentNotFound from './ContentNotFound';
import { useVoicePresenceDirectory } from '../hooks/useVoicePresenceDirectory';
import AppShell from './shell/AppShell';
import ContentPane from './shell/ContentPane';
import './Dashboard.css';

const CHAT_NOTIFICATION_MUTES_KEY = 'zapen-chat-notification-mutes';
const DM_NOTIFICATION_MUTES_KEY = 'zapen-dm-notification-mutes';
const EMPTY_VOICE_PRESENCE = { count: 0, participants: [], speakingCount: 0 };

function isMuteEntryActive(until) {
  return until === null || new Date(until) > new Date();
}

function getStoredChatNotificationMutes() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CHAT_NOTIFICATION_MUTES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, until]) => until === null || (typeof until === 'string' && isMuteEntryActive(until)))
    );
  } catch { return {}; }
}

function getStoredDmNotificationMutes() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(DM_NOTIFICATION_MUTES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, until]) => until === null || (typeof until === 'string' && isMuteEntryActive(until)))
    );
  } catch { return {}; }
}

function getRouteState(pathname) {
  const childRoute = matchPath('/channel/:chatId/:childId', pathname);
  if (childRoute) return { kind: 'channel-child', chatId: childRoute.params.chatId, childId: childRoute.params.childId };
  const roomRoute = matchPath('/channel/:chatId', pathname);
  if (roomRoute) return { kind: 'channel', chatId: roomRoute.params.chatId };
  const dmRoute = matchPath('/friends/:friendId', pathname);
  if (dmRoute) return { kind: 'dm', friendId: dmRoute.params.friendId };
  if (pathname === '/friends') return { kind: 'friends' };
  if (pathname === '/invites') return { kind: 'invites' };
  if (pathname === '/p2p') return { kind: 'p2p' };
  return { kind: 'route-not-found' };
}

function getDocumentTitle(selectedChat, selectedRootChat) {
  if (!selectedChat) return 'Zapen';
  if (selectedChat.parent_chat_id && selectedRootChat) return `Zapen | ${selectedChat.name} | ${selectedRootChat.name}`;
  return `Zapen | ${selectedChat.name}`;
}

const Dashboard = ({ session, user, onSignOut }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [myChats, setMyChats] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [friends, setFriends] = useState([]);
  const [pendingFriends, setPendingFriends] = useState([]);
  const [invites, setInvites] = useState([]);
  const [dmProfile, setDmProfile] = useState(null);
  const [dmMutualChats, setDmMutualChats] = useState([]);
  const [dmDetailsLoading, setDmDetailsLoading] = useState(false);

  // Right panel / member state
  const [roomMembers, setRoomMembers] = useState([]);
  const [roomMembership, setRoomMembership] = useState(null);
  const [kickTarget, setKickTarget] = useState(null);
  const [voiceModerationTarget, setVoiceModerationTarget] = useState(null);
  const [voiceModerationBusy, setVoiceModerationBusy] = useState(false);
  const [voiceModerationError, setVoiceModerationError] = useState('');
  const [voiceLockBusy, setVoiceLockBusy] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');

  // Modal state
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const [channelSettingsChat, setChannelSettingsChat] = useState(null);
  const [showCreateChildModal, setShowCreateChildModal] = useState(false);
  const [newChildChatName, setNewChildChatName] = useState('');
  const [creatingChildChat, setCreatingChildChat] = useState(false);
  const [createChildChatError, setCreateChildChatError] = useState('');
  const [showDmNotificationMuteModal, setShowDmNotificationMuteModal] = useState(false);

  // Unread / notifications
  const [unreadChatIds, setUnreadChatIds] = useState(new Set());
  const [unreadDmIds, setUnreadDmIds] = useState(new Set());
  const [mutedChatNotifications, setMutedChatNotifications] = useState(() => getStoredChatNotificationMutes());
  const [mutedDmNotifications, setMutedDmNotifications] = useState(() => getStoredDmNotificationMutes());
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const [dmMenuOpen, setDmMenuOpen] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const inviteSubRef = useRef(null);
  const friendsRequesterSubRef = useRef(null);
  const dmSubRef = useRef(null);
  const chatSubsRef = useRef([]);
  const presenceChannelRef = useRef(null);
  const selectedChatRef = useRef(null);
  const selectedFriendRef = useRef(null);
  const viewRef = useRef('chats');
  const mutedChatNotificationsRef = useRef(mutedChatNotifications);
  const mutedDmNotificationsRef = useRef(mutedDmNotifications);
  const audioContextRef = useRef(null);
  const invitesReadyRef = useRef(false);
  const pendingFriendsReadyRef = useRef(false);
  const prevInviteCountRef = useRef(0);
  const prevPendingFriendsCountRef = useRef(0);

  const playNotificationSound = async () => {
    if (typeof window === 'undefined') return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!audioContextRef.current) audioContextRef.current = new AudioCtx();
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { return; }
    }
    const now = ctx.currentTime;
    const master = ctx.createGain();
    const toneA = ctx.createOscillator();
    const toneB = ctx.createOscillator();
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(1500, now);
    lowpass.Q.setValueAtTime(0.7, now);
    toneA.type = 'sine';
    toneA.frequency.setValueAtTime(720, now);
    toneA.frequency.exponentialRampToValueAtTime(560, now + 0.18);
    toneB.type = 'triangle';
    toneB.frequency.setValueAtTime(1080, now);
    toneB.frequency.exponentialRampToValueAtTime(820, now + 0.12);
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.07, now + 0.02);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    toneA.connect(lowpass);
    toneB.connect(lowpass);
    lowpass.connect(master);
    master.connect(ctx.destination);
    toneA.start(now); toneB.start(now);
    toneA.stop(now + 0.3); toneB.stop(now + 0.22);
  };

  // ── Route derivation ─────────────────────────────────────────────────────
  const routeState = getRouteState(location.pathname);
  const currentView = routeState.kind === 'channel' || routeState.kind === 'channel-child'
    ? 'chats' : routeState.kind;
  const routeChatId = routeState.kind === 'channel' || routeState.kind === 'channel-child'
    ? routeState.chatId : null;
  const routeFriendId = routeState.kind === 'dm' ? routeState.friendId : null;
  const selectedRootChat = routeChatId
    ? myChats.find((chat) => chat.id === routeChatId && !chat.parent_chat_id) || null : null;
  const selectedChildChat = routeState.kind === 'channel-child' && selectedRootChat
    ? myChats.find((chat) => chat.id === routeState.childId && chat.parent_chat_id === selectedRootChat.id) || null : null;
  const selectedChat = routeState.kind === 'channel-child' ? selectedChildChat : selectedRootChat;
  const selectedChatAccessRoot = selectedRootChat;
  const selectedFriend = routeFriendId
    ? friends.filter((f) => f.status === 'accepted')
        .map((f) => getOtherProfile(f, user.id))
        .find((p) => p?.id === routeFriendId) || null
    : null;
  const rootChats = myChats.filter((chat) => !chat.parent_chat_id);
  const selectedChatChildren = selectedRootChat
    ? myChats.filter((chat) => chat.parent_chat_id === selectedRootChat.id)
        .slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
    : [];

  const syncOnlineUserIds = (presenceState) => {
    const nextOnlineUserIds = new Set();
    Object.values(presenceState || {}).forEach((presences) => {
      presences.forEach((p) => { if (p.user_id) nextOnlineUserIds.add(p.user_id); });
    });
    setOnlineUserIds(nextOnlineUserIds);
  };

  // ── Initial subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    loadData().finally(() => setInitialDataLoaded(true));

    const channel = supabase
      .channel('dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_invites', filter: `invited_user_id=eq.${user.id}` }, () => loadInvites())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'friends', filter: `addressee_id=eq.${user.id}` }, () => loadFriends())
      .subscribe();

    const friendsRequesterChannel = supabase
      .channel('dashboard-friends-requester')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'friends', filter: `requester_id=eq.${user.id}` }, () => loadFriends())
      .subscribe();

    const presenceChannel = supabase
      .channel('online-status', { config: { presence: { key: user.id } } })
      .on('presence', { event: 'sync' }, () => syncOnlineUserIds(presenceChannel.presenceState()))
      .on('presence', { event: 'join' }, () => syncOnlineUserIds(presenceChannel.presenceState()))
      .on('presence', { event: 'leave' }, () => syncOnlineUserIds(presenceChannel.presenceState()))
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({ user_id: user.id, opened_at: new Date().toISOString() });
          [0, 150, 500, 1500].forEach((delay) => {
            window.setTimeout(() => syncOnlineUserIds(presenceChannel.presenceState()), delay);
          });
        }
      });

    const dmChannel = supabase
      .channel('dashboard-dm')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'direct_messages', filter: `receiver_id=eq.${user.id}` }, (payload) => {
        const msg = payload.new;
        const isActiveDm = viewRef.current === 'dm' && selectedFriendRef.current?.id === msg.sender_id;
        if (isActiveDm) {
          supabase.rpc('mark_dm_read', { p_sender_id: msg.sender_id }).then(() => {});
        } else {
          setUnreadDmIds(prev => new Set([...prev, msg.sender_id]));
          if (!isMuteEntryActive(mutedDmNotificationsRef.current[msg.sender_id])) playNotificationSound();
        }
      })
      .subscribe();

    inviteSubRef.current = channel;
    friendsRequesterSubRef.current = friendsRequesterChannel;
    dmSubRef.current = dmChannel;
    presenceChannelRef.current = presenceChannel;

    return () => {
      if (inviteSubRef.current) supabase.removeChannel(inviteSubRef.current);
      if (friendsRequesterSubRef.current) supabase.removeChannel(friendsRequesterSubRef.current);
      if (dmSubRef.current) supabase.removeChannel(dmSubRef.current);
      if (presenceChannelRef.current) supabase.removeChannel(presenceChannelRef.current);
      chatSubsRef.current.forEach(ch => supabase.removeChannel(ch));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chat message subscriptions for unread tracking
  useEffect(() => {
    chatSubsRef.current.forEach(ch => supabase.removeChannel(ch));
    chatSubsRef.current = [];
    if (myChats.length === 0) return;
    chatSubsRef.current = myChats.map(chat =>
      supabase
        .channel(`dashboard-msg-${chat.id}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `chat_id=eq.${chat.id}` }, (payload) => {
          const msg = payload.new;
          if (msg.sender_id === user.id) return;
          const isActiveChat = viewRef.current === 'chats' && selectedChatRef.current?.id === msg.chat_id;
          if (isActiveChat) {
            markChatRead(msg.chat_id);
          } else {
            setUnreadChatIds(prev => new Set([...prev, msg.chat_id]));
            if (!isMuteEntryActive(mutedChatNotificationsRef.current[msg.chat_id])) playNotificationSound();
          }
        })
        .subscribe()
    );
  }, [myChats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Audio context priming
  useEffect(() => {
    const primeAudio = () => { if (!audioContextRef.current) return; audioContextRef.current.resume().catch(() => {}); };
    document.addEventListener('pointerdown', primeAudio);
    document.addEventListener('keydown', primeAudio);
    return () => {
      document.removeEventListener('pointerdown', primeAudio);
      document.removeEventListener('keydown', primeAudio);
    };
  }, []);

  // Keep refs in sync
  useEffect(() => { selectedChatRef.current = selectedChat; }, [selectedChat]);
  useEffect(() => { selectedFriendRef.current = selectedFriend; }, [selectedFriend]);
  useEffect(() => { viewRef.current = currentView; }, [currentView]);
  useEffect(() => { mutedChatNotificationsRef.current = mutedChatNotifications; }, [mutedChatNotifications]);
  useEffect(() => { mutedDmNotificationsRef.current = mutedDmNotifications; }, [mutedDmNotifications]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = getDocumentTitle(selectedChat, selectedRootChat);
  }, [selectedChat, selectedRootChat]);

  useEffect(() => {
    window.localStorage.setItem(CHAT_NOTIFICATION_MUTES_KEY, JSON.stringify(mutedChatNotifications));
  }, [mutedChatNotifications]);

  useEffect(() => {
    window.localStorage.setItem(DM_NOTIFICATION_MUTES_KEY, JSON.stringify(mutedDmNotifications));
  }, [mutedDmNotifications]);

  useEffect(() => {
    if (!invitesReadyRef.current) { invitesReadyRef.current = true; }
    else if (invites.length > prevInviteCountRef.current) { playNotificationSound(); }
    prevInviteCountRef.current = invites.length;
  }, [invites.length]);

  useEffect(() => {
    if (!pendingFriendsReadyRef.current) { pendingFriendsReadyRef.current = true; }
    else if (pendingFriends.length > prevPendingFriendsCountRef.current) { playNotificationSound(); }
    prevPendingFriendsCountRef.current = pendingFriends.length;
  }, [pendingFriends.length]);

  // Clear unread badge on DM open
  useEffect(() => {
    if (currentView === 'dm' && selectedFriend) {
      setUnreadDmIds(prev => { const s = new Set(prev); s.delete(selectedFriend.id); return s; });
    }
  }, [currentView, selectedFriend?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark chat read on open
  useEffect(() => {
    if (currentView === 'chats' && selectedChat && (routeState.kind === 'channel' || routeState.kind === 'channel-child')) {
      markChatRead(selectedChat.id);
      setUnreadChatIds(prev => { const s = new Set(prev); s.delete(selectedChat.id); return s; });
    }
  }, [currentView, routeState.kind, selectedChat?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset right-panel state on chat change
  useEffect(() => {
    setInviteUsername('');
    setInviteStatus('');
    setKickTarget(null);
    setVoiceModerationTarget(null);
    setVoiceModerationError('');
    setShowCreateChildModal(false);
    setNewChildChatName('');
    setCreateChildChatError('');
    setRoomMembers([]);
    setRoomMembership(null);
  }, [selectedChat?.id]);

  useEffect(() => {
    setDmMenuOpen(false);
    setShowDmNotificationMuteModal(false);
  }, [selectedFriend?.id, currentView]);

  // Load DM details
  useEffect(() => {
    let cancelled = false;
    const loadDmDetails = async () => {
      if (currentView !== 'dm' || !selectedFriend?.id) {
        setDmProfile(null); setDmMutualChats([]); setDmDetailsLoading(false); return;
      }
      setDmDetailsLoading(true);
      const [{ data: fullProfile }, { data: mutualChatsData }] = await Promise.all([
        supabase.from('profiles').select('id, username, display_name, created_at').eq('id', selectedFriend.id).single(),
        myChats.length > 0
          ? supabase.from('chats').select('id, name, parent_chat_id, chat_members!inner(user_id)').in('id', myChats.map(c => c.id)).eq('chat_members.user_id', selectedFriend.id)
          : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;
      setDmProfile(fullProfile || selectedFriend);
      setDmMutualChats((mutualChatsData || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
      setDmDetailsLoading(false);
    };
    loadDmDetails();
    return () => { cancelled = true; };
  }, [currentView, selectedFriend?.id, myChats]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading ─────────────────────────────────────────────────────────
  const loadData = async () => {
    await Promise.all([loadProfile(), loadChats(), loadFavorites(), loadInvites(), loadFriends(), loadUnreadChatIds(), loadUnreadDmIds()]);
  };

  const loadUnreadChatIds = async () => {
    const { data } = await supabase.rpc('get_unread_chat_ids');
    setUnreadChatIds(new Set(data || []));
  };

  const loadUnreadDmIds = async () => {
    const { data } = await supabase.rpc('get_unread_dm_sender_ids');
    setUnreadDmIds(new Set(data || []));
  };

  const markChatRead = (chatId) => {
    supabase.rpc('mark_chat_read', { p_chat_id: chatId }).then(() => {});
  };

  const loadProfile = async () => {
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    setProfile(data);
  };

  const loadChats = async () => {
    const { data } = await supabase.from('chats').select('*');
    setMyChats(data || []);
  };

  const loadFavorites = async () => {
    const { data } = await supabase.from('favorites').select('chat_id').eq('user_id', user.id);
    setFavorites(new Set((data || []).map(f => f.chat_id)));
  };

  const loadInvites = async () => {
    const { data } = await supabase
      .from('chat_invites')
      .select('*, chats(name), profiles!chat_invites_invited_by_profiles_fkey(username, display_name)')
      .eq('invited_user_id', user.id).eq('status', 'pending');
    setInvites(data || []);
  };

  const loadFriends = async () => {
    const { data } = await supabase
      .from('friends')
      .select('*, requester:profiles!friends_requester_id_profiles_fkey(id, username, display_name), addressee:profiles!friends_addressee_id_profiles_fkey(id, username, display_name)')
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);
    const all = data || [];
    setPendingFriends(all.filter(f => f.status === 'pending' && f.addressee_id === user.id));
    setFriends(all);
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const isChatNotificationsMuted = (chatId) => isMuteEntryActive(mutedChatNotifications[chatId]);
  const isDmNotificationsMuted = (userId) => isMuteEntryActive(mutedDmNotifications[userId]);

  const muteChatNotifications = (chatId, durationMinutes) => {
    const until = durationMinutes === null ? null : new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
    setMutedChatNotifications(prev => ({ ...prev, [chatId]: until }));
  };

  const unmuteChatNotifications = (chatId) => {
    setMutedChatNotifications(prev => { const next = { ...prev }; delete next[chatId]; return next; });
  };

  const muteDmNotifications = (userId, durationMinutes) => {
    const until = durationMinutes === null ? null : new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
    setMutedDmNotifications(prev => ({ ...prev, [userId]: until }));
  };

  const unmuteDmNotifications = (userId) => {
    setMutedDmNotifications(prev => { const next = { ...prev }; delete next[userId]; return next; });
  };

  const handleCreateRoom = async (name) => {
    const { data: newChat, error } = await supabase
      .from('chats').insert({ name, created_by: user.id }).select().single();
    if (error) return { error: error.code === '23505' ? 'Name taken.' : error.message };
    await loadChats();
    navigate(`/channel/${newChat.id}`);
    return {};
  };

  const handleCreateChildChat = async () => {
    const name = newChildChatName.trim();
    if (!name || !selectedChatAccessRoot) return;
    setCreatingChildChat(true);
    setCreateChildChatError('');
    const { data: newChat, error } = await supabase
      .from('chats').insert({ name, created_by: user.id, parent_chat_id: selectedChatAccessRoot.id }).select().single();
    if (error) {
      setCreatingChildChat(false);
      setCreateChildChatError(error.code === '23505' ? 'Name taken in this chat.' : error.message);
      return;
    }
    setCreatingChildChat(false);
    setNewChildChatName('');
    setShowCreateChildModal(false);
    await loadChats();
    navigate(`/channel/${selectedChatAccessRoot.id}/${newChat.id}`);
  };

  const handleStarToggle = async (chatId) => {
    if (favorites.has(chatId)) {
      await supabase.from('favorites').delete().eq('user_id', user.id).eq('chat_id', chatId);
    } else {
      await supabase.from('favorites').insert({ user_id: user.id, chat_id: chatId });
    }
    loadFavorites();
  };

  const handleChannelIconUpdated = (chatId, iconUrl) => {
    setMyChats(current => current.map(c => c.id === chatId ? { ...c, icon_url: iconUrl } : c));
  };

  const handleInviteAccept = async (invite) => {
    await loadChats();
    navigate(`/channel/${invite.chat_id}`);
  };

  const handleInvite = async () => {
    setInviteStatus('');
    const username = trimUsername(inviteUsername);
    if (!username || !selectedChatAccessRoot) return;
    let profileId;
    try { profileId = await getUserIdByUsername(username); }
    catch (lookupError) { setInviteStatus(lookupError.message); return; }
    if (!profileId) { setInviteStatus('We could not find a user with that username.'); return; }
    if (profileId === user.id) { setInviteStatus("That's you."); return; }
    if (roomMembers.some((m) => m.user_id === profileId)) { setInviteStatus('Already in chat.'); return; }
    const { data: existingInvites } = await supabase
      .from('chat_invites').select('id').eq('chat_id', selectedChatAccessRoot.id)
      .eq('invited_user_id', profileId).eq('status', 'pending').limit(1);
    if (existingInvites?.length) { setInviteStatus('Already invited.'); return; }
    const { data: newInvite, error } = await supabase.from('chat_invites').insert({
      chat_id: selectedChatAccessRoot.id, invited_by: user.id, invited_user_id: profileId,
    }).select('id').single();
    if (error) {
      setInviteStatus(error.code === '23505' ? 'Already invited.' : error.message);
    } else {
      setInviteStatus('Invite sent!');
      setInviteUsername('');
      if (newInvite?.id) supabase.functions.invoke('send-invite-email', { body: { invite_id: newInvite.id } }).catch(() => {});
    }
  };

  const handleKick = async (memberId, durationMinutes) => {
    if (!selectedChatAccessRoot) return;
    if (durationMinutes === null) {
      await supabase.from('chat_members').update({ banned: true }).eq('chat_id', selectedChatAccessRoot.id).eq('user_id', memberId);
    } else {
      const until = new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
      await supabase.from('chat_members').update({ kicked_until: until }).eq('chat_id', selectedChatAccessRoot.id).eq('user_id', memberId);
    }
    setKickTarget(null);
    const { data } = await supabase
      .from('chat_members').select('*, profiles!chat_members_user_id_profiles_fkey(id, username, display_name)').eq('chat_id', selectedChatAccessRoot.id);
    setRoomMembers(data || []);
  };

  const refreshSelectedChatMembers = async () => {
    if (!selectedChatAccessRoot) return [];
    const { data } = await supabase
      .from('chat_members').select('*, profiles!chat_members_user_id_profiles_fkey(id, username, display_name)').eq('chat_id', selectedChatAccessRoot.id);
    setRoomMembers(data || []);
    return data || [];
  };

  const handleFriendInviteAccept = async (friendRow) => {
    await supabase.from('friends').update({ status: 'accepted' }).eq('id', friendRow.id);
    loadFriends();
  };

  const handleFriendInviteDecline = async (friendRow) => {
    await supabase.from('friends').update({ status: 'declined' }).eq('id', friendRow.id);
    loadFriends();
  };

  const ensureFreshSession = async () => {
    const { data } = await supabase.auth.refreshSession();
    if (!data.session) throw new Error('Your session has expired. Please sign in again.');
  };

  const handleVoiceLockToggle = async (locked) => {
    if (!selectedChat) return;
    setVoiceModerationError('');
    setVoiceLockBusy(true);
    try {
      await ensureFreshSession();
      const { data, error } = await supabase.functions.invoke('voice-moderation', {
        body: { action: 'set-room-lock', chatId: selectedChat.id, locked },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setMyChats((current) => current.map((chat) =>
        chat.id === selectedChat.id ? { ...chat, voice_locked: Boolean(data.voiceLocked) } : chat
      ));
    } catch (err) {
      setVoiceModerationError(err?.message || 'Unable to update room voice settings.');
    } finally {
      setVoiceLockBusy(false);
    }
  };

  const handleVoiceModeration = async (action, memberId, durationMinutes = undefined) => {
    if (!selectedChat) return;
    setVoiceModerationError('');
    setVoiceModerationBusy(true);
    try {
      await ensureFreshSession();
      const { error, data } = await supabase.functions.invoke('voice-moderation', {
        body: { action, chatId: selectedChat.id, targetUserId: memberId, durationMinutes },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const refreshedMembers = await refreshSelectedChatMembers();
      const refreshedTarget = refreshedMembers.find((m) => m.user_id === memberId) || null;
      setVoiceModerationTarget(refreshedTarget);
      if (action === 'mute-member' || action === 'remove-member' || action === 'ban-member') {
        setVoiceModerationTarget(null);
      }
    } catch (err) {
      setVoiceModerationError(err?.message || 'Unable to update voice moderation.');
    } finally {
      setVoiceModerationBusy(false);
    }
  };

  // ── Derived display data ──────────────────────────────────────────────────
  const displayName = profile?.display_name || profile?.username || user.email;
  const acceptedFriends = friends
    .filter(f => f.status === 'accepted')
    .slice()
    .sort((a, b) => {
      const aP = getOtherProfile(a, user.id);
      const bP = getOtherProfile(b, user.id);
      const aName = (aP?.display_name || aP?.username || '').toLocaleLowerCase();
      const bName = (bP?.display_name || bP?.username || '').toLocaleLowerCase();
      return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
    })
    .map(f => {
      const p = getOtherProfile(f, user.id);
      return { ...p, friendRowId: f.id };
    });

  const totalInviteBadge = invites.length + pendingFriends.length;
  const favoriteChats = rootChats.filter(c => favorites.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
  const nonFavoriteChats = rootChats.filter(c => !favorites.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
  const voicePresenceByRoom = useVoicePresenceDirectory(myChats.map((chat) => chat.id));
  const getVoicePresence = (chatId) => voicePresenceByRoom[chatId] || EMPTY_VOICE_PRESENCE;
  const rootChatHasUnread = (chatId) =>
    unreadChatIds.has(chatId) || myChats.some((chat) => chat.parent_chat_id === chatId && unreadChatIds.has(chat.id));

  const routeIsLoading = !initialDataLoaded && (
    routeState.kind === 'channel' || routeState.kind === 'channel-child' || routeState.kind === 'dm'
  );
  const shouldShowRoomNotFound = initialDataLoaded && (
    (routeState.kind === 'channel' && !selectedRootChat) ||
    (routeState.kind === 'channel-child' && (!selectedRootChat || !selectedChildChat))
  );
  const shouldShowDmNotFound = initialDataLoaded && routeState.kind === 'dm' && !selectedFriend;
  const shouldShowRouteNotFound = routeState.kind === 'route-not-found';
  const showEmptyRoomsWelcome = currentView === 'chats'
    && routeState.kind !== 'channel' && routeState.kind !== 'channel-child';

  const isRoomOwner = roomMembership?.role === 'owner';
  const canCreateChildChats = Boolean(
    selectedChatAccessRoot && !selectedChatAccessRoot.parent_chat_id &&
    (isRoomOwner || selectedChatAccessRoot.created_by === user.id)
  );

  const dmDisplayProfile = dmProfile || selectedFriend;
  const dmDisplayName = dmDisplayProfile?.display_name || dmDisplayProfile?.username || 'Unknown';
  const isSelectedDmMuted = selectedFriend ? isDmNotificationsMuted(selectedFriend.id) : false;
  const isSelectedRoomVoiceLocked = Boolean(selectedChat?.voice_locked);
  const selectedRoomVoicePresence = selectedChat ? getVoicePresence(selectedChat.id) : EMPTY_VOICE_PRESENCE;
  const selectedRoomVoiceParticipantsByUserId = new Map(
    selectedRoomVoicePresence.participants.map((p) => [p.userId, p])
  );
  const sortedRoomMembers = roomMembers.slice().sort((a, b) => {
    const aName = (a.profiles?.display_name || a.profiles?.username || '').toLocaleLowerCase();
    const bName = (b.profiles?.display_name || b.profiles?.username || '').toLocaleLowerCase();
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  });
  const onlineRoomMembers = sortedRoomMembers.filter((m) => onlineUserIds.has(m.user_id));
  const offlineRoomMembers = sortedRoomMembers.filter((m) => !onlineUserIds.has(m.user_id));

  // ── Prop groups ───────────────────────────────────────────────────────────
  const navProps = {
    favorites,
    favoriteChats,
    nonFavoriteChats,
    rootChats,
    selectedRootChat,
    selectedChat,
    selectedChatChildren,
    currentView,
    selectedFriend,
    acceptedFriends,
    onlineUserIds,
    unreadChatIds,
    unreadDmIds,
    pendingFriends,
    totalInviteBadge,
    invites,
    getVoicePresence,
    rootChatHasUnread,
    user,
    showEmptyRoomsWelcome,
    onCreateRoom: handleCreateRoom,
    canCreateSubRoom: canCreateChildChats,
    onOpenCreateSubRoom: () => { setCreateChildChatError(''); setShowCreateChildModal(true); },
  };

  const detailProps = {
    currentView,
    // Room members
    selectedChat,
    selectedChatAccessRoot,
    isRoomOwner,
    roomMembers,
    onlineRoomMembers,
    offlineRoomMembers,
    inviteUsername,
    inviteStatus,
    onInviteUsernameChange: (v) => setInviteUsername(v),
    onInvite: handleInvite,
    selectedRoomVoicePresence,
    isSelectedRoomVoiceLocked,
    voiceLockBusy,
    voiceModerationError,
    selectedRoomVoiceParticipantsByUserId,
    onKickTarget: (id) => setKickTarget(id),
    onVoiceModerationTarget: (m) => setVoiceModerationTarget(m),
    onVoiceLockToggle: handleVoiceLockToggle,
    user,
    // DM details
    selectedFriend,
    dmDisplayProfile,
    dmDisplayName,
    dmMutualChats,
    dmDetailsLoading,
    isSelectedDmMuted,
    dmMenuOpen,
    onDmMenuOpen: (open) => setDmMenuOpen(open),
    onUnmuteDm: () => unmuteDmNotifications(selectedFriend?.id),
    onOpenDmMuteModal: () => setShowDmNotificationMuteModal(true),
  };

  const modalProps = {
    showChannelSettings,
    channelSettingsChat,
    onOpenChannelSettings: () => { setChannelSettingsChat(selectedRootChat); setShowChannelSettings(true); },
    onCloseChannelSettings: () => { setShowChannelSettings(false); setChannelSettingsChat(null); },
    onChannelIconUpdated: handleChannelIconUpdated,
    showCreateChildModal,
    newChildChatName,
    creatingChildChat,
    createChildChatError,
    selectedChatAccessRoot,
    onNewChildChatName: (v) => setNewChildChatName(v),
    onCreateChildChat: handleCreateChildChat,
    onCloseCreateChildModal: () => setShowCreateChildModal(false),
    showDmNotificationMuteModal,
    selectedFriend,
    onMuteDm: (minutes) => { muteDmNotifications(selectedFriend?.id, minutes); setShowDmNotificationMuteModal(false); },
    onCloseDmMuteModal: () => setShowDmNotificationMuteModal(false),
    kickTarget,
    onKick: handleKick,
    onCancelKick: () => setKickTarget(null),
    voiceModerationTarget,
    voiceModerationBusy,
    voiceModerationError,
    onVoiceModeration: handleVoiceModeration,
    onCancelVoiceModeration: () => setVoiceModerationTarget(null),
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppShell
      user={user}
      profile={profile}
      displayName={displayName}
      onSignOut={onSignOut}
      currentView={currentView}
      navProps={navProps}
      detailProps={detailProps}
      modalProps={modalProps}
      hasUnreadNav={
        favoriteChats.some(c => rootChatHasUnread(c.id)) ||
        nonFavoriteChats.some(c => rootChatHasUnread(c.id)) ||
        acceptedFriends.some(f => unreadDmIds.has(f.id))
      }
      showSubChatToggle={currentView === 'chats' && Boolean(selectedChat) && Boolean(selectedChatAccessRoot)}
      hasUnreadSubChats={
        currentView === 'chats' && Boolean(selectedChatAccessRoot) &&
        [selectedChatAccessRoot, ...selectedChatChildren].some(c => c && unreadChatIds.has(c.id))
      }
    >
      {currentView === 'p2p' && (
        <P2PChat
          initialPeerId={profile?.username || null}
          user={user}
          onSignOut={onSignOut}
        />
      )}

      {routeIsLoading && (
        <div className="content-welcome">
          <div className="welcome-glyph">✦</div>
          <h2 className="welcome-heading">Loading…</h2>
          <p className="welcome-sub">Checking this route and loading your data.</p>
        </div>
      )}

      {showEmptyRoomsWelcome && (
        <div className="content-welcome">
          <div className="welcome-glyph">✦</div>
          <h2 className="welcome-heading">Select a room</h2>
          <p className="welcome-sub">Choose a room from the sidebar, or create a new one.</p>
        </div>
      )}

      {shouldShowRoomNotFound && (
        <ContentNotFound variant="room" onNavigateHome={() => navigate('/friends')} />
      )}

      {shouldShowRouteNotFound && (
        <ContentNotFound variant="route" onNavigateHome={() => navigate('/friends')} />
      )}

      {currentView === 'chats' && selectedChat && selectedChatAccessRoot && (
        <ContentPane
          selectedChat={selectedChat}
          selectedChatAccessRoot={selectedChatAccessRoot}
          selectedChatChildren={selectedChatChildren}
          unreadChatIds={unreadChatIds}
          canCreateChildChats={canCreateChildChats}
          onOpenCreateChildModal={() => {
            setCreateChildChatError('');
            setShowCreateChildModal(true);
          }}
        >
          <RoomChat
            key={selectedChat.id}
            chatId={selectedChat.id}
            accessChatId={selectedChatAccessRoot.id}
            session={session}
            user={user}
            voicePresence={getVoicePresence(selectedChat.id)}
            onBack={() => navigate('/friends')}
            onMembersChange={(members, membership) => {
              setRoomMembers(members);
              setRoomMembership(membership);
            }}
            onDeleted={() => {
              loadChats();
              loadFavorites();
              loadUnreadChatIds();
              navigate(selectedChat.parent_chat_id ? `/channel/${selectedChatAccessRoot.id}` : '/friends');
            }}
            onLeft={() => {
              loadChats();
              loadFavorites();
              loadUnreadChatIds();
              navigate('/friends');
            }}
            isFavorite={favorites.has(selectedChat.id)}
            onToggleFavorite={() => handleStarToggle(selectedChat.id)}
            isNotificationsMuted={isChatNotificationsMuted(selectedChat.id)}
            onMuteNotifications={(durationMinutes) => muteChatNotifications(selectedChat.id, durationMinutes)}
            onUnmuteNotifications={() => unmuteChatNotifications(selectedChat.id)}
          />
        </ContentPane>
      )}

      {currentView === 'friends' && (
        <FriendsPanel
          user={user}
          friends={friends}
          onRefresh={loadFriends}
          onlineUserIds={onlineUserIds}
          onFriendSelect={(friendId) => navigate(`/friends/${friendId}`)}
        />
      )}

      {shouldShowDmNotFound && (
        <ContentNotFound variant="dm" onNavigateHome={() => navigate('/friends')} />
      )}

      {currentView === 'dm' && selectedFriend && (
        <DMChat
          key={selectedFriend.id}
          otherUser={selectedFriend}
          currentUser={profile ? { ...user, ...profile } : user}
          onMarkRead={(senderId) => {
            setUnreadDmIds(prev => { const s = new Set(prev); s.delete(senderId); return s; });
          }}
        />
      )}

      {currentView === 'invites' && (
        <InvitesPanel
          invites={invites}
          pendingFriends={pendingFriends}
          onlineUserIds={onlineUserIds}
          onRefresh={loadInvites}
          onAccept={handleInviteAccept}
          onFriendAccept={handleFriendInviteAccept}
          onFriendDecline={handleFriendInviteDecline}
        />
      )}
    </AppShell>
  );
};

export default Dashboard;
